/**
 * Tiny client-side cart store, persisted to localStorage. No external dep.
 * Subscribers re-render through useSyncExternalStore in the hook below.
 */

export type CartItem = {
  variantId: string;
  productSlug: string;
  productTitle: string;
  variantName: string;
  unitPriceCents: number;
  quantity: number;
  image?: string;
};

type CartState = {
  items: CartItem[];
  discountCode?: string;
};

const STORAGE_KEY = "glg.cart.v1";
const subs = new Set<() => void>();
let cached: CartState = { items: [] };
let initialized = false;

function load(): CartState {
  if (typeof window === "undefined") return { items: [] };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { items: [] };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return { items: [] };
    return parsed;
  } catch {
    return { items: [] };
  }
}

function persist() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cached));
  } catch {
    /* quota */
  }
}

function notify() {
  subs.forEach((fn) => fn());
}

export function ensureCartHydrated() {
  if (initialized || typeof window === "undefined") return;
  cached = load();
  initialized = true;
}

/**
 * Called from an effect after mount so the first client render matches the
 * server HTML (empty cart), then swaps in the persisted cart.
 */
export function hydrateCartFromStorage() {
  if (initialized || typeof window === "undefined") return;
  const stored = load();
  initialized = true;
  const merged = cached.items.length
    ? mergeCarts(stored, cached)
    : stored;
  cached = merged;
  persist();
  notify();
}

function mergeCarts(base: CartState, incoming: CartState): CartState {
  const items = [...base.items];
  for (const item of incoming.items) {
    const idx = items.findIndex((i) => i.variantId === item.variantId);
    if (idx >= 0) items[idx] = { ...items[idx], quantity: items[idx].quantity + item.quantity };
    else items.push({ ...item });
  }
  return { items, discountCode: incoming.discountCode ?? base.discountCode };
}

/** Keep other tabs/windows in sync with this one. */
export function watchCartStorage(): () => void {
  if (typeof window === "undefined") return () => {};
  const onStorage = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY) return;
    cached = load();
    initialized = true;
    notify();
  };
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}

export function getCart(): CartState {
  return cached;
}

export function subscribeCart(fn: () => void): () => void {
  subs.add(fn);
  return () => {
    subs.delete(fn);
  };
}

export function addToCart(item: CartItem) {
  ensureCartHydrated();
  const existing = cached.items.find((i) => i.variantId === item.variantId);
  const items = existing
    ? cached.items.map((i) =>
        i.variantId === item.variantId ? { ...i, quantity: i.quantity + item.quantity } : i,
      )
    : [...cached.items, { ...item }];
  cached = { ...cached, items };
  persist();
  notify();
  // Fire-and-forget A/B conversion tracking. Lazy import keeps cart.ts free of
  // the supabase client at module load (cart.ts runs early in the bundle).
  if (typeof window !== "undefined") {
    void import("./ab").then((m) => m.trackAbConversion("add_to_cart")).catch(() => {});
  }
}

export function updateQuantity(variantId: string, quantity: number) {
  ensureCartHydrated();
  cached = {
    ...cached,
    items: cached.items
      .map((i) => (i.variantId === variantId ? { ...i, quantity } : i))
      .filter((i) => i.quantity > 0),
  };
  persist();
  notify();
}

export function removeFromCart(variantId: string) {
  ensureCartHydrated();
  cached = { ...cached, items: cached.items.filter((i) => i.variantId !== variantId) };
  persist();
  notify();
}

export function clearCart() {
  cached = { items: [] };
  persist();
  notify();
}

export function setDiscount(code: string | undefined) {
  ensureCartHydrated();
  cached = { ...cached, discountCode: code };
  persist();
  notify();
}

export function cartSubtotalCents(state: CartState = getCart()): number {
  return state.items.reduce((sum, i) => sum + i.unitPriceCents * i.quantity, 0);
}

export function cartItemCount(state: CartState = getCart()): number {
  return state.items.reduce((sum, i) => sum + i.quantity, 0);
}
